import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import { toast } from "react-toastify";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg";
import ChevronRightWhiteIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import TickSmIcon from "../../../../assets/img/action-center/tick-sm.svg";
import BnberryLogo from "../../../../assets/img/action-center/bnberry-logo.png";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import { bnberryAction } from "../../../../api";
import { isInternalUser, isSupplierUser } from "../../../../constants/utils";
import {
  BNBERRY_ACTION,
  BNBERRY_CARD_STATE,
  BNBERRY_COPY,
  BNBERRY_FORM_URL,
  BNBERRY_HEAP_ID,
} from "./bnberryConstants";
import "./BnberryCard.scss";

// EO-660: standalone Action Centre card recommending BnBerry (Airbnb/VRBO) opt-in.
// RECOMMENDATION → hotel can opt in (Maybe Later / Opt-in Now); PENDING → the form
// was opened but can't be tracked, so the card nudges (Open Form / Dismiss). The
// backend decides which state to send and removes the card once the corridor opens.
const BnberryCard = ({ data, onDismiss, cardPosition, hotelName }) => {
  const [optingIn, setOptingIn] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  // Optimistically flip to PENDING after a successful opt-in so the CTA updates
  // without waiting for the next Action Centre refetch.
  const [localState, setLocalState] = useState(data?.cardState);

  const formUrl = data?.formUrl || BNBERRY_FORM_URL;
  const isPending = localState === BNBERRY_CARD_STATE.PENDING;
  // Internal (AM/Supply) users see the same CTAs as the hotel does (EO-1268). They were
  // hidden to keep internal clicks out of the Slack/email alerts, but a card with no
  // buttons is a confusing thing to demo on a screen-share, and showing the real card
  // was judged worth the alert noise — suppressing alerts for internal clicks is a
  // separate decision, deliberately deferred, so alerts still fire as normal.
  //
  // CLIENT users stay view-only: this opened up for HotelTrader's own staff, not buyers.
  const canAct = isSupplierUser() || isInternalUser();

  // Distinguish the two states in Heap's card-shown event (spec: track BnBerry
  // non-opted-in vs opted-in), riding the shared Recommendation-Card-Viewed event.
  const cardRef = useHeapCardViewed("bnberry", cardPosition, hotelName, {
    card_state: isPending ? "pending" : "recommendation",
  });

  // Open a blank tab first so a blocked popup is detectable — window.open with
  // `noopener` always returns null, which would hide the failure. Sever the
  // opener manually to keep the security `noopener` gave us. Returns the window
  // (null when the popup was blocked).
  const openForm = () => {
    const formWindow = window.open("", "_blank");
    if (formWindow) {
      formWindow.opener = null;
      formWindow.location.href = formUrl;
    }
    return formWindow;
  };

  const handleOptIn = async () => {
    setOptingIn(true);
    // Open the form synchronously inside the click's user-gesture window — a
    // window.open after the await below is treated as an unsolicited popup and
    // blocked by default (see REVIEW.md). If it was still blocked, don't record
    // the opt-in or flip the card; ask the user to allow pop-ups instead.
    if (!openForm()) {
      toast.error(
        "Please allow pop-ups to open the BnBerry form, then try again."
      );
      setOptingIn(false);
      return;
    }
    try {
      // Records the click and fires the AM Slack alert (backend).
      await bnberryAction(data?.propertyId, BNBERRY_ACTION.OPT_IN);
      setLocalState(BNBERRY_CARD_STATE.PENDING);
    } catch (error) {
      // Track opt-in failures in Rapid7 via the shared telemetry logger.
      getLogger().error("bnberry.optin_failed", {
        type: "Error",
        property_id: data?.propertyId,
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      toast.error("Couldn't record your opt-in. Please try again.");
    } finally {
      setOptingIn(false);
    }
  };

  // Recommendation "Maybe Later" is a session-only hide (handled by onDismiss). The pending card's
  // "Dismiss" is permanent: persist it server-side first, then hide locally, so it won't return.
  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await bnberryAction(data?.propertyId, BNBERRY_ACTION.DISMISS);
      onDismiss();
    } catch (error) {
      // Track dismiss failures in Rapid7 via the shared telemetry logger.
      getLogger().error("bnberry.dismiss_failed", {
        type: "Error",
        property_id: data?.propertyId,
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      toast.error("Couldn't dismiss the card. Please try again.");
    } finally {
      setDismissing(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`bnberry-card${isPending ? " bnberry-card--pending" : ""}`}
    >
      <div className="bnberry-card-body">
        <div className="bnberry-card-title-block">
          <div className="bnberry-card-badge">
            <img src={TrendingUpIcon} alt="" className="badge-icon" />
            <span>{BNBERRY_COPY.tag}</span>
          </div>
          <div className="bnberry-card-heading">
            <h3 className="bnberry-card-title">
              {isPending
                ? BNBERRY_COPY.pendingHeadline
                : BNBERRY_COPY.recommendationHeadline}
            </h3>
            <p className="bnberry-card-description">
              {isPending
                ? BNBERRY_COPY.pendingBody
                : BNBERRY_COPY.recommendationDescription}
            </p>
          </div>
        </div>

        <div className="bnberry-card-partner">
          <div className="bnberry-card-partner-header">
            <div className="bnberry-card-partner-channels">
              <span className="bnberry-card-partner-name">
                {BNBERRY_COPY.partnerChannels}
              </span>
              <span className="bnberry-card-partner-via">
                {BNBERRY_COPY.partnerVia}
              </span>
              <img
                src={BnberryLogo}
                alt="BnBerry"
                className="bnberry-card-partner-logo"
              />
            </div>
            {isPending && (
              <span className="bnberry-card-partner-tick">
                <img src={TickSmIcon} alt="" />
              </span>
            )}
          </div>
          <p className="bnberry-card-partner-desc">
            {BNBERRY_COPY.partnerDescription}
          </p>
        </div>
      </div>

      {canAct && (
        <div className="bnberry-card-actions">
          <div className="bnberry-card-actions-left">
            <Button
              id={
                isPending ? BNBERRY_HEAP_ID.dismiss : BNBERRY_HEAP_ID.maybeLater
              }
              data-heap-id={
                isPending ? BNBERRY_HEAP_ID.dismiss : BNBERRY_HEAP_ID.maybeLater
              }
              appearance="link"
              className="maybe-later-btn"
              loading={isPending && dismissing}
              onClick={isPending ? handleDismiss : onDismiss}
            >
              {isPending ? "Dismiss" : "Maybe Later"}
            </Button>
          </div>
          {isPending ? (
            <Button
              id={BNBERRY_HEAP_ID.openForm}
              data-heap-id={BNBERRY_HEAP_ID.openForm}
              appearance="primary"
              className="open-now-btn"
              onClick={openForm}
            >
              <span>Open Form</span>
              <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
            </Button>
          ) : (
            <Button
              id={BNBERRY_HEAP_ID.optIn}
              data-heap-id={BNBERRY_HEAP_ID.optIn}
              appearance="primary"
              className="open-now-btn"
              loading={optingIn}
              onClick={handleOptIn}
            >
              <span>Opt-in Now</span>
              <img src={ChevronRightWhiteIcon} alt="" className="btn-chevron" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

BnberryCard.propTypes = {
  data: PropTypes.shape({
    propertyId: PropTypes.number,
    propertyCode: PropTypes.string,
    cardState: PropTypes.oneOf([
      BNBERRY_CARD_STATE.RECOMMENDATION,
      BNBERRY_CARD_STATE.PENDING,
    ]),
    formUrl: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default BnberryCard;
